<script setup>
import {ref, computed, onMounted, nextTick, toRef, watch} from 'vue';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { UseMoneyStore } from '@/stores/moneyStore';
import { UseProductStore } from '@/stores/productStore';
import GlobalHeader from '@/components/GlobalHeader.vue';
import {VueSpinner} from "vue3-spinners";
import {UseOrderStore} from "@/stores/orderStore";
import {useScrollToError} from "@/composables/scrollToError";
import {useIban} from "@/composables/validIban";
import {useProductsValidation} from "@/composables/productPageValidations";
import {useReturnStore} from "@/stores/returnStore";
import ProductItem from '@/components/ProductItem.vue';
import DescriptionHeader from "@/components/DescriptionHeader.vue";
import ReturnMethods from "@/components/ReturnMethods/ReturnMethods.vue";
import {useOrderDataStore} from "@/stores/orderDataStore";
import {useI18n} from "vue-i18n";
// import {ElMessage} from "element-plus";


const IBAN_DIGITS_GR = 25;
const IBAN_DIGITS_CY = 26;
const IBAN_DIGITS_SPACES_GR = 31;
const IBAN_DIGITS_SPACES_CY = 32;


// Store initialization
const moneyStore = UseMoneyStore();
const productStore = UseProductStore();
const orderStore = UseOrderStore();
const returnStore = useReturnStore();
const orderDataStore = useOrderDataStore();

const { t } = useI18n()
const router = useRouter();

const ibanDigits = ref('');

const loading = ref(true); // while fetching products from API

const products = computed(() => orderDataStore.lines)
const noReturnMessage = ref(false);

const bankOptions = computed(() => {
  if (orderDataStore.shippingCountry === 'GR') {
    return [
      t('banks.gr.alpha'),
      t('banks.gr.attica'),
      t('banks.gr.eurobank'),
      t('banks.gr.national'),
      t('banks.gr.pankritia'),
      t('banks.gr.piraeus'),
    ];
  } else if (orderDataStore.shippingCountry === 'CY') {
    return [
      t('banks.cy.alpha_cy'),
      t('banks.cy.astro'),
      t('banks.cy.boc'),
      t('banks.cy.eurobank_cy'),
      t('banks.cy.hellenic'),
    ];
  } else {
    return [];
  }
});

const selectedBank = ref('')
const customBankName = ref('')
const isOtherSelected = computed(() => selectedBank.value === '__other__')

function handleSelect(value) {
  if (value !== '__other__') {
    moneyStore.ibanBank = value
  } else {
    moneyStore.ibanBank = '' // Clear previous value
  }
}

function confirmOtherBank() {
  const trimmed = customBankName.value.trim();
  moneyStore.ibanBank = trimmed || "";
}

onMounted(() => {
  orderDataStore.fetchOrder();
  loading.value = false;
  ibanDigits.value = moneyStore.iban;
});

console.log("products: ", products)
const atLeastOneChecked = computed(() => products.value.some((product) => productStore.isChecked[product.id]));
const ibanLength = computed(() => (orderDataStore.shippingCountry === 'GR' ? IBAN_DIGITS_GR : IBAN_DIGITS_CY));
const { isValidIban, formattedIban, updateIban } = useIban(ibanDigits, moneyStore);

const { rules } = useProductsValidation(products, productStore, atLeastOneChecked, ibanLength, isValidIban);
const v$ = useVuelidate(rules, { products, selectedProducts:toRef(productStore, 'selectedProducts'), selectedHowReturnMoney:toRef(moneyStore, 'selectedHowReturnMoney'), ibanDigits, beneficiaryName:toRef(moneyStore, 'beneficiaryName'), ibanBank:toRef(moneyStore, 'ibanBank') });

const nonRefundable = (product) => product.refundable === false;


const goToLoginPage = () => {
  orderStore.$reset();
  moneyStore.$reset();
  router.push("/");
};

const goToHowToReturnPage = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) {
    useScrollToError();
    return;
  }

  console.log(formattedIban, updateIban)
  if(returnStore.finalReturn < 0 && orderDataStore.shippingCountry==='GR' && moneyStore.selectedHowReturnMoney!=='credit') {
    noReturnMessage.value = true
    await nextTick();  // Wait for the DOM update
    useScrollToError();
    return;
  }

  router.push('/howToReturn');
};

const getPlaceholder = (product) => {
  const reason = productStore.returnReasons[product.id];

  if (reason === "defective") {
    return t('productsPage.placeholders.defective');
  }

  if (reason === "wrongProduct") {
    return t('productsPage.placeholders.wrongProduct');
  }

  return t('productsPage.placeholders.default');
};

watch(productStore.selectedProducts, () => {
  noReturnMessage.value = false;
})

watch(toRef(moneyStore, 'selectedHowReturnMoney'), () => {
  noReturnMessage.value = false;
});


</script>


<template>
  <div class="flex flex-col min-h-screen w-full">
    <div class="flex-grow sm:flex-none">

      <GlobalHeader :onBack="goToLoginPage" />
      <DescriptionHeader :label="t('productsPage.step1')"/>
      <div v-if="orderDataStore.loading" class="grid place-content-center mt-10 mb-4">
        <VueSpinner size="20"/>
      </div>
      <section v-else class="flex justify-center">
        <div class="grid bg-bw-white border overflow-hidden container mt-10 mb-4 px-6">
          <div
              v-for="(product,index) in products"
              :key="product.id"
              class="mt-0 pt-6"
              :class="nonRefundable(product) ? 'bg-bw-lightGrey bg-opacity-25': 'bg-bw-white'"
          >
            <label>
              <ProductItem :product="product" page="ProductsPage"/>
            </label>

            <span v-if="nonRefundable(product)" class="grid responsive-body-xxsmall text-primary-red pt-3 px-3">
              <text><strong>{{ t('productsPage.nonRefundable.title') }}</strong><br> 
                {{ t('productsPage.nonRefundable.text') }} <a class="font-bold whitespace-nowrap focus:outline-2 focus:outline-offset-2 focus:outline-bw-black" href="mailto:support@thefashionproject.gr">support@thefashionproject.gr</a>.”
              </text>
            </span>
            <form v-if="productStore.isChecked[product.id]" class="pt-10">

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

                <!-- LEFT: Return Reasons -->
                <div class="responsive-body-small">
                  <p class="font-semibold text-bw-lightDarkGrey responsive-title-7 pb-5">
                    {{ t('productsPage.whyReturn') }}
                  </p>

                  <!-- Size -->
                  <label :for="'size-' + product.id" class="flex items-center mb-3">
                    <input
                        type="radio"
                        :name="'reason-' + product.id"
                        :id="'size-' + product.id"
                        value="size"
                        v-model="productStore.returnReasons[product.id]"
                        class="accent-bw-black mr-2"
                        @change="productStore.handleReasonChange(product)"
                    />
                    {{ t('productsPage.reasons.size') }}
                  </label>

                  <!-- Changed Mind -->
                  <label :for="'opinion-' + product.id" class="flex items-center mb-3">
                    <input
                        type="radio"
                        :name="'reason-' + product.id"
                        :id="'opinion-' + product.id"
                        value="changedMind"
                        v-model="productStore.returnReasons[product.id]"
                        class="accent-bw-black mr-2"
                        @change="productStore.handleReasonChange(product)"
                    />
                    {{ t('productsPage.reasons.changedMind') }}
                  </label>

                  <!-- Defective -->
                  <label :for="'defective-' + product.id" class="flex items-center mb-3">
                    <input
                        type="radio"
                        :name="'reason-' + product.id"
                        :id="'defective-' + product.id"
                        value="defective"
                        v-model="productStore.returnReasons[product.id]"
                        class="accent-bw-black mr-2"
                        @change="productStore.handleReasonChange(product)"
                    />
                    {{ t('productsPage.reasons.defective') }}
                  </label>

                  <!-- Wrong Product -->
                  <div class="mb-4">
                    <label :for="'wrong-' + product.id" class="flex items-center">
                      <input
                          type="radio"
                          :name="'reason-' + product.id"
                          :id="'wrong-' + product.id"
                          value="wrongProduct"
                          v-model="productStore.returnReasons[product.id]"
                          class="accent-bw-black mr-2"
                          @change="productStore.handleReasonChange(product)"
                      />
                      <span>{{ t('productsPage.reasons.wrongProduct') }}</span>
                    </label>
                    <span class="ml-5 text-bw-lightDarkGrey italic responsive-body-xsmall block">
                      {{ t('productsPage.reasons.wrongProductDesc') }}
                    </span>
                  </div>

                  <!-- No Reason -->
                  <label :for="'noreason-' + product.id" class="flex items-center mb-3">
                    <input
                        type="radio"
                        :name="'reason-' + product.id"
                        :id="'noreason-' + product.id"
                        value="noReason"
                        v-model="productStore.returnReasons[product.id]"
                        class="accent-bw-black mr-2"
                        @change="productStore.handleReasonChange(product)"
                    />
                    {{ t('productsPage.reasons.noReason') }}
                  </label>

                  <!-- Validation Error -->
                  <p
                      v-if="v$.products[index].returnReasons.$error"
                      class="text-primary-red responsive-body-small mt-2"
                  >
                    {{ v$.products[index].returnReasons.$errors[0].$message }}
                  </p>
                </div>

                <!-- RIGHT: Description -->
                <div v-if="productStore.returnReasons[product.id] &&productStore.returnReasons[product.id] !== 'noReason'">
                  <p class="font-semibold text-bw-lightDarkGrey responsive-title-7 pb-5">
                    {{ t('productsPage.descriptionTitle') }}
                  </p>

                  <div class="w-full md:w-5/6">
                    <textarea
                        class="border pl-2 py-1 w-full box-border focus:outline-bw-black placeholder:responsive-body-small"
                        :name="'describe-' + product.id"
                        :id="'describe-' + product.id"
                        rows="4"
                        :placeholder="getPlaceholder(product)"
                        v-model="productStore.productDesc[product.id]"
                    ></textarea>

                    <p
                        v-if="v$.products[index].productDesc.$error"
                        class="text-primary-red responsive-body-small mt-2"
                    >
                      {{ v$.products[index].productDesc.$errors[0].$message }}
                    </p>
                  </div>
                </div>
              </div>
            </form>
            <br>
            <hr v-if="index<products.length-1" class="border-bw-darkLightGrey bg-background-white">
          </div>
        </div>
      </section>


      <section class="flex justify-center">
        <form v-if="!orderDataStore.loading && atLeastOneChecked" class="grid bg-background-white border overflow-hidden container responsive-body-small mb-4 px-6 py-3">
          <p class="font-semibold text-bw-lightDarkGrey pb-5 responsive-title-7">{{ t('productsPage.preferences') }}</p>

          <!--Money Return Methods -->
          <ReturnMethods :update-iban="updateIban" :formatted-iban-value="formattedIban"/>

          <form v-if="moneyStore.selectedHowReturnMoney==='iban'" class="mt-5">
            <p class="font-semibold pb-5 text-bw-grey responsive-title-7">{{ t('productsPage.ibanTitle') }}</p>
            <div class="flex formdiv items-center responsive-body-small">
              <div class="flex items-center border rounded w-1/2 boxwidth">
                <!-- Prefilled prefix -->
                <span class="border-r px-1 py-1 bg-bw-lightGrey text-bw-grey">{{ orderDataStore.shippingCountry }}</span>
                <!-- User input for IBAN digits-->
                <input
                    type="text"
                    id="iban"
                    name="iban"
                    v-model="formattedIban"
                    :placeholder="t('productsPage.ibanPlaceholder')"
                    class=" flex-1 rounded w-full p-1 focus:outline-bw-black placeholder:responsive-body-xsmall"
                    :maxlength="orderDataStore.shippingCountry === 'GR' ? IBAN_DIGITS_SPACES_GR : orderDataStore.shippingCountry === 'CY' ? IBAN_DIGITS_SPACES_CY : 0"
                    @input="updateIban"
                />
              </div>
              <br/>

              <input
                  type="text"
                  id="name"
                  name="info"
                  v-model="moneyStore.beneficiaryName"
                  :placeholder="t('productsPage.beneficiaryPlaceholder')"
                  class="border rounded w-1/2 sm:mx-3 p-1 boxwidth focus:outline-bw-black placeholder:responsive-body-xsmall"
              />
            </div>
            <p v-if="v$.ibanDigits.$error" class="text-primary-red responsive-body-small" :class="{ 'is-invalid': v$.ibanDigits.$error }">{{ v$.ibanDigits.$errors[0].$message }}</p>
            <p v-if="v$.beneficiaryName.$error" class="text-red-600 responsive-body-small" :class="{ 'is-invalid': v$.beneficiaryName.$error }">{{ v$.beneficiaryName.$errors[0].$message }}</p>
            <div>
              <p class="font-semibold pb-5 text-bw-grey responsive-title-7 mt-5">
                {{ t('productsPage.selectBank') }}
              </p>
              <el-select
                  v-model="selectedBank"
                  :placeholder="moneyStore.ibanBank ? moneyStore.ibanBank : t('productsPage.selectBankPlaceholder')"
                  class="w-full md:w-1/2"
                  @change="handleSelect"
              >
                <el-option
                    v-for="bank in bankOptions"
                    :key="bank"
                    :label="bank"
                    :value="bank"
                />
                <el-option
                    :label="t('productsPage.otherBank')"
                    value="__other__"
                />
              </el-select>

              <!-- Input for custom bank -->
              <div v-if="isOtherSelected" class="mt-4">
                <el-input
                    v-model="customBankName"
                    :placeholder="t('productsPage.otherBankPlaceholder')"
                    class="w-full md:w-1/2"
                    @input="confirmOtherBank"

                />
              </div>
            </div>

            <p v-if="v$.ibanBank.$error" class="text-red-600 responsive-body-small" :class="{ 'is-invalid': v$.ibanBank.$error }">{{ v$.ibanBank.$errors[0].$message }}</p>

          </form>
          <p v-if="v$.selectedHowReturnMoney.$error" class="text-primary-red responsive-body-small" :class="{ 'is-invalid': v$.selectedHowReturnMoney.$error }">{{ v$.selectedHowReturnMoney.$errors[0].$message }}</p>
        </form>
      </section>

      <p v-if="v$.selectedProducts.$error" class="text-primary-red text-center mb-2 responsive-body-small" :class="{ 'is-invalid': v$.selectedProducts.$error }">{{ v$.selectedProducts.$errors[0].$message }}</p>
      <p v-if="noReturnMessage && orderDataStore.shippingCountry==='GR'" class="text-primary-red text-center responsive-body-small mb-2" :class="{ 'is-invalid': noReturnMessage }" v-html="t('productsPage.noReturnMessage.lowAmount')"></p>

    </div>
    <div v-if="!orderDataStore.loading" class="flex sticky md:relative bottom-0 md:bottom-auto bg-background-default justify-center items-center">
      <button
          @click.prevent="goToHowToReturnPage"
          class="border rounded bg-bw-black text-bw-white responsive-body-large mb-2 py-3.5 px-1 overflow-hidden container hover:bg-bw-darkGrey focus:outline-2 focus:outline-offset-2 focus:outline-bw-black"
          :class="(v$.$invalid || (noReturnMessage && orderDataStore.shippingCountry==='GR')) ? 'bg-bw-darkGrey' : 'bg-bw-black'"
      >
        {{ t('productsPage.continue') }}
      </button>
    </div>

  </div>

</template>

<style scoped>
.checkbox{
  transform: scale(1.5);
}

@media (max-width: 450px) {
  .boxwidth{
    width: 100%;

  }
  .formdiv{
    display:block;
  }
}

</style>

